export type StepId = "class" | "share" | "login" | "canvas" | "lesson";

export const NAME_SELF_ENTRY =
  "학생 이름은 선생님이 넣지 않아도 됩니다. 학생이 처음 로그인할 때 직접 입력합니다.";

export const CREATE_CLASS_ITEMS = [
  NAME_SELF_ENTRY,
  "학생 마지막 번호까지 계정이 만들어집니다(최대 30). 쓰지 않는 번호는 나중에 사용 중지할 수 있습니다.",
  "학생 계정의 처음 비밀번호는 학급을 만든 뒤 「학생 계정」 탭과 학생별 안내문에서 볼 수 있습니다.",
  "계정 접두어는 만든 뒤 바꿀 수 없습니다.",
];

const SHARE_ITEM =
  "「학생별 안내문 인쇄」로 아이마다 한 장씩 나눠 줍니다. 반 전체가 나오는 「명단 인쇄」·「CSV 내려받기」는 선생님 보관용입니다.";
const LOGIN_ITEM =
  "학생은 로그인 화면에서 아이디와 비밀번호를 넣고, 처음 들어오면 자기 이름을 직접 입력합니다. 선생님이 이름을 미리 넣을 필요는 없습니다.";
const LAST_LOGIN_ITEM = "‘마지막 로그인’으로 학생이 들어왔는지 확인합니다.";

export const ACCOUNT_MANAGEMENT_ITEMS = [
  "비밀번호를 잊은 학생은 ‘비밀번호 초기화’로 처음 비밀번호로 되돌립니다. 처음 비밀번호는 학생별 안내문에 적혀 있습니다.",
  "학생이 이름을 잘못 입력했을 때만 이름을 고치거나 지웁니다. 지우면 학생이 다음 로그인 때 다시 입력합니다.",
  "쓰지 않는 번호는 ‘사용 중지’로 막고, 필요하면 다시 사용할 수 있습니다. 계정 삭제는 없습니다.",
  LAST_LOGIN_ITEM,
];

export const STUDENTS_ITEMS = [
  SHARE_ITEM,
  LOGIN_ITEM,
  ...ACCOUNT_MANAGEMENT_ITEMS,
];

export const CANVASES_ITEMS = [
  "개인형은 학생 번호마다 한 칸, 모둠형은 모둠마다 한 칸, 일반은 빈 캔버스로 시작합니다. 발표 수업은 학생마다 따로 발표 캔버스가 생깁니다.",
  "같은 학급 학생은 이 탭에 있는 캔버스를 모두 열 수 있습니다. 발표 수업의 학생 캔버스만은 그 학생과 선생님만 엽니다.",
  "제목 옆 버튼으로 이름 변경·복제·휴지통·이력을 쓸 수 있습니다.",
];

export const LESSON_ITEMS = [
  "집중모드: 학생은 자기 프레임에만 그립니다(개인형 캔버스).",
  "도움모드: 학생이 친구 프레임도 고칠 수 있습니다(개인형 캔버스).",
  "마감: 개인형·모둠형 캔버스에서 프레임을 마감하면 더 고칠 수 없고, 마감 풀기로 되돌립니다.",
  "휴대폰에서는 캔버스가 보기 전용으로 열립니다(태블릿·컴퓨터는 편집 가능).",
];

export const SETTINGS_ITEMS = [
  "학급 이름은 언제든 바꿀 수 있습니다.",
  "학기가 끝나면 학급을 보관하세요. 학생 로그인과 편집이 멈추고, 설정에서 다시 진행할 수 있습니다.",
];

export type GuideStep = {
  id: StepId;
  title: string;
  description: string;
  shortcut: string;
  /** One short line for the tooltip bar. */
  tip: string;
  details: string[];
};

export const GUIDE_STEPS: GuideStep[] = [
  {
    id: "class",
    title: "학급 만들기",
    description:
      "학급 이름과 계정 접두어, 학생 마지막 번호를 정하면 번호별 학생 계정이 한 번에 만들어집니다.",
    shortcut: "학급 만들기",
    tip: "학급을 만들면 번호별 학생 계정이 한 번에 생깁니다.",
    details: [
      "계정 접두어는 학생 아이디 앞부분입니다. 예: class3-1 → class3-1_1, class3-1_2 …",
      ...CREATE_CLASS_ITEMS,
    ],
  },
  {
    id: "share",
    title: "학생 계정 나눠 주기",
    description:
      "학생 계정 탭의 「학생별 안내문 인쇄」로 아이디와 처음 비밀번호가 적힌 안내문을 아이마다 한 장씩 나눠 줍니다. 명단과 CSV는 선생님 보관용입니다.",
    shortcut: "학생 계정 탭",
    tip: "학생별 안내문을 인쇄해 아이마다 한 장씩 나눠 주세요.",
    details: [SHARE_ITEM],
  },
  {
    id: "login",
    title: "학생 첫 로그인 확인",
    description:
      "학생은 로그인 화면에서 아이디와 초기 비밀번호로 들어와 자기 이름을 직접 입력합니다. 선생님이 이름을 넣을 필요는 없고, 학생 계정 탭의 ‘마지막 로그인’에서 들어왔는지 확인만 하면 됩니다.",
    shortcut: "학생 계정 탭",
    tip: "학생이 로그인해 이름을 직접 입력하면 완료입니다.",
    details: [LOGIN_ITEM, LAST_LOGIN_ITEM],
  },
  {
    id: "canvas",
    title: "캔버스 만들기",
    description:
      "캔버스 탭에서 제목을 정하고 템플릿(개인형·모둠형·일반·발표 수업)을 고르면 수업할 캔버스가 만들어집니다.",
    shortcut: "캔버스 만들기",
    tip: "템플릿을 골라 수업할 캔버스를 만드세요.",
    details: CANVASES_ITEMS,
  },
  {
    id: "lesson",
    title: "수업 시작",
    description:
      "캔버스를 열면 같은 학급 학생들이 함께 그릴 수 있습니다. 개인형 캔버스에서는 집중모드·도움모드로, 개인형·모둠형 캔버스에서는 마감으로 수업을 이끌어 보세요.",
    shortcut: "캔버스 탭",
    tip: "캔버스를 열면 학생들과 함께 그릴 수 있습니다.",
    details: LESSON_ITEMS,
  },
];

export const STUDENT_STEPS = [
  "로그인 화면에서 선생님께 받은 아이디와 비밀번호를 넣습니다.",
  "처음 들어오면 자기 이름을 입력합니다.",
  "열고 싶은 캔버스를 고릅니다.",
  "내 프레임에 그립니다.",
  "비밀번호를 잊었다면 선생님께 말씀드리세요.",
  "휴대폰에서는 캔버스를 보기만 할 수 있어요. 그리려면 태블릿이나 컴퓨터를 쓰세요.",
  "발표 수업 캔버스는 나와 선생님만 봐요. 「+」로 슬라이드를 더하고, 다 만들면 「완료」를 눌러요. 「발표」를 누르면 전체 화면으로 넘겨 볼 수 있어요.",
];

export const PRESENTATION_ITEMS = [
  "「발표 수업」 템플릿으로 캔버스를 만들면 학생마다 자기 발표 캔버스가 생깁니다. 학생 캔버스는 그 학생과 선생님만 열 수 있습니다.",
  "학생은 빈 슬라이드 1장으로 시작해 「+」로 슬라이드를 더하고, 「◀」「▶」로 순서를 바꾸고, 「🗑」로 지웁니다(최대 30장).",
  "학생이 오른쪽 「완료」를 누르면 발표 수업 화면의 학생 카드와 「완료 n / 전체」에 바로 보입니다. 완료한 뒤에도 고칠 수 있습니다.",
  "발표 수업을 열면 학생 카드가 번호순으로 보입니다. 「열기」로 학생 캔버스에 들어가고, 「발표」로 전체 화면 슬라이드쇼를 엽니다. 교사 컴퓨터를 TV에 연결해 두면 앞에 나온 학생의 발표를 반 전체가 봅니다.",
  "슬라이드쇼는 →나 Space로 넘기고 ←로 돌아가며, Esc로 끝냅니다. 마지막 장 다음에 나오는 버튼으로 다음 학생의 발표로 넘어갑니다.",
];

export const GALLERY_ITEMS = [
  "학생은 마감한 자기 프레임을 「학교 밖 갤러리」에 자랑할 수 있습니다. 다른 학교 친구와 선생님이 보고 반응을 남깁니다.",
  "갤러리에는 그림과 학교·학급 이름만 보입니다. 학생 이름이나 번호는 보이지 않습니다.",
  "학급 화면의 「갤러리」 탭에서 올라간 작품을 보고, 「내리기」로 내릴 수 있습니다. 내린 작품은 「다시 허용」을 누르기 전까지 학생이 다시 올릴 수 없습니다.",
  "학교에서 학생 작품을 밖에 보이지 않기로 했다면, 학생에게 이 기능을 쓰지 말라고 안내하고 올라간 작품은 내려 주세요.",
];

/** null means the page cannot tell yet (e.g. the class list knows no roster). */
export type StepDone = Record<StepId, boolean | null>;
